/**
 * Admin Summary Hook - Fetches admin center summary data
 * Epic 61: Admin Kozpont Dashboard
 */

import { getAdminSummary, type AdminSummary } from '@/api/admin-center';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseAdminSummaryResult {
  summary: AdminSummary | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const REFETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useAdminSummary(): UseAdminSummaryResult {
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // M-012 fix: Track request ordering to prevent stale response overwrites
  const requestIdRef = useRef(0);

  const fetchSummary = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAdminSummary();
      // Only update if this is still the latest request
      if (currentRequestId === requestIdRef.current) {
        setSummary(response.data);
      }
    } catch (err) {
      if (currentRequestId === requestIdRef.current) {
        const message =
          err instanceof Error ? err.message : 'Nem sikerult betolteni az osszefoglalot';
        setError(message);
        console.error('Error fetching admin summary:', err);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void fetchSummary();

    intervalRef.current = setInterval(() => {
      void fetchSummary();
    }, REFETCH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Invalidate any in-flight requests on unmount
      requestIdRef.current++;
    };
  }, [fetchSummary]);

  return {
    summary,
    isLoading,
    error,
    refetch: fetchSummary,
  };
}
